import { bootstrapApplication } from '@angular/platform-browser';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { importProvidersFrom, LOCALE_ID, provideZoneChangeDetection } from '@angular/core';
import { provideServiceWorker } from '@angular/service-worker';
import { HttpClient } from '@angular/common/http';
import { enableProdMode } from '@angular/core';
import { registerLocaleData } from '@angular/common';

// Import locale data for all supported languages
import localeDe from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en';
import localeEnGB from '@angular/common/locales/en-GB';
import localeFr from '@angular/common/locales/fr';
import localeEs from '@angular/common/locales/es';
import localeSv from '@angular/common/locales/sv';
import localeCs from '@angular/common/locales/cs';
import localeDa from '@angular/common/locales/da';
import localeEt from '@angular/common/locales/et';
import localeFi from '@angular/common/locales/fi';
import localeIt from '@angular/common/locales/it';
import localeNb from '@angular/common/locales/nb';

import { AppComponent } from './app/app.component';
import { environment } from './environments/environment';
import { reducers, metaReducers } from './app/+state/reducers';
import { effects } from './app/+state/effects';
import { WaGeolocationService } from '@ng-web-apis/geolocation';
import { MultiTranslateHttpLoader } from 'ngx-translate-multi-http-loader';
import { routes } from './app/app.routes';
import { provideDateFnsAdapter } from '@angular/material-date-fns-adapter';
import { MAT_DATE_LOCALE } from '@angular/material/core';

// Import date-fns locales for Material datepicker
import { de, enUS, enGB, fr, es, sv, cs, da, fi, it, nb } from 'date-fns/locale';
import { provideCustomIcons } from './app/shared/icons/provide-custom-icons';
import { provideRouterStore } from '@ngrx/router-store';
import { clientIdentificationInterceptor } from './app/core/client-identification.interceptor';

if (environment.production) {
  enableProdMode();
}

// Register all locale data for Angular's built-in pipes
registerLocaleData(localeDe, 'de');
registerLocaleData(localeEn, 'en');
registerLocaleData(localeEnGB, 'en-GB');
registerLocaleData(localeFr, 'fr');
registerLocaleData(localeEs, 'es');
registerLocaleData(localeSv, 'sv');
registerLocaleData(localeCs, 'cs');
registerLocaleData(localeDa, 'da');
registerLocaleData(localeEt, 'et');
registerLocaleData(localeFi, 'fi');
registerLocaleData(localeIt, 'it');
registerLocaleData(localeNb, 'nb');

// Map language codes to Angular locale IDs
const LOCALE_MAP: { [key: string]: string } = {
  'de': 'de',
  'en': 'en',
  'fr': 'fr',
  'es': 'es',
  'sv': 'sv',
  'cs': 'cs',
  'da': 'da',
  'et': 'et',
  'fi': 'fi',
  'it': 'it',
  'no': 'nb'  // Norwegian Bokmål
};

// Map language codes to date-fns locales for Material datepicker
const DATE_FNS_LOCALE_MAP: { [key: string]: Locale } = {
  'de': de,
  'en': enUS,
  'fr': fr,
  'es': es,
  'sv': sv,
  'cs': cs,
  'da': da,
  'et': enUS, // Estonian not available in date-fns, fallback to English
  'fi': fi,
  'it': it,
  'no': nb
};

/**
 * Factory function to get the locale ID based on stored language preference.
 * Reads from the same localStorage key used by TranslateService.
 * Called once at bootstrap time.
 */
export function getLocaleId(): string {
  const langCode = localStorage.getItem('NG_TRANSLATE_LANG_KEY') || 'en';
  return LOCALE_MAP[langCode] || 'en';
}

/**
 * Factory function to get the date-fns locale for Material datepicker.
 * Reads from the same localStorage key used by TranslateService.
 * Called once at bootstrap time.
 */
export function getDateFnsLocale(): Locale {
  const langCode = localStorage.getItem('NG_TRANSLATE_LANG_KEY') || 'en';
  return DATE_FNS_LOCALE_MAP[langCode] || enUS;
}

export function createTranslateLoader(http: HttpClient) {
  const languageSources = [
    // Database stored
    { prefix: environment.serviceBase + '/translations/nested?lang=', suffix: '' },
  ];

  // JSON Asset
  if (environment.jsoni18n) {
    languageSources.push({ prefix: './assets/i18n/', suffix: '.json' });
  }

  return new MultiTranslateHttpLoader(http, languageSources);
}


bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),provideRouter(routes, withInMemoryScrolling({ scrollPositionRestoration: 'top' })),
    provideAnimations(),
    provideHttpClient(withInterceptors([clientIdentificationInterceptor]), withInterceptorsFromDi()),
    provideCustomIcons(),
    provideStore(reducers, { metaReducers }),
    provideEffects(effects),
    provideRouterStore(),
    provideStoreDevtools({
      maxAge: 25,
      logOnly: environment.production,
      connectInZone: true
    }),
    // Dynamic locale based on user's language preference
    { provide: LOCALE_ID, useFactory: getLocaleId },
    { provide: MAT_DATE_LOCALE, useFactory: getDateFnsLocale },
    provideDateFnsAdapter(),
    // No need for DATE_PIPE_DEFAULT_OPTIONS or MAT_DATE_FORMATS
    // Date formatting will follow the locale automatically
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: createTranslateLoader,
          deps: [HttpClient],
        },
      })
    ),
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.production
    }),
    WaGeolocationService,
  ]
}).catch(err => console.log(err));
